/**
 * 语言状态管理器
 * 负责全局语言状态的存储和管理
 */
import { DocumentIdentitySource, DocumentIdentityEventType, } from '@readsavor/types';
import { DocumentIdentityService } from '../../document/services/DocumentIdentityService.js';
import { TextDocumentStorageProvider, PdfDocumentStorageProvider } from '@readsavor/utils';
// 默认语言设置
const DEFAULTS = {
    SOURCE_LANGUAGE: 'auto',
    TARGET_LANGUAGE: 'en',
};
/**
 * 语言状态管理器类
 * 使用单例模式管理全局语言状态
 */
export default class LanguageStateManager {
    // 私有构造函数
    constructor() {
        // 订阅者列表
        this.sourceLanguageListeners = [];
        this.targetLanguageListeners = [];
        // 直接使用默认值初始化语言偏好
        this.sourceLanguage = DEFAULTS.SOURCE_LANGUAGE;
        this.targetLanguage = DEFAULTS.TARGET_LANGUAGE;
        // 订阅DocumentIdentity变更事件
        const documentIdentityService = DocumentIdentityService.getInstance();
        documentIdentityService.addIdentityChangeListener(this.handleDocumentIdentityChange.bind(this));
    }
    /**
     * 获取单例实例
     * @returns LanguageStateManager实例
     */
    static getInstance() {
        if (!LanguageStateManager.instance) {
            LanguageStateManager.instance = new LanguageStateManager();
        }
        return LanguageStateManager.instance;
    }
    /**
     * 获取首选源语言
     * @returns 源语言代码
     */
    getPreferredSourceLanguage() {
        return this.sourceLanguage;
    }
    /**
     * 获取首选目标语言
     * @returns 目标语言代码
     */
    getPreferredTargetLanguage() {
        return this.targetLanguage;
    }
    /**
     * 设置首选源语言
     * @param languageCode 源语言代码
     */
    setPreferredSourceLanguage(languageCode) {
        if (this.sourceLanguage !== languageCode) {
            this.sourceLanguage = languageCode;
            this.notifySourceLanguageListeners();
        }
    }
    /**
     * 设置首选目标语言
     * @param languageCode 目标语言代码
     */
    setPreferredTargetLanguage(languageCode) {
        if (this.targetLanguage !== languageCode) {
            this.targetLanguage = languageCode;
            this.notifyTargetLanguageListeners();
        }
    }
    /**
     * 通知源语言变化的订阅者
     */
    notifySourceLanguageListeners() {
        this.sourceLanguageListeners.forEach(listener => {
            try {
                listener(this.sourceLanguage);
            }
            catch (error) {
                console.error('Failed to notify source language listeners:', error);
            }
        });
    }
    /**
     * 通知目标语言变化的订阅者
     */
    notifyTargetLanguageListeners() {
        this.targetLanguageListeners.forEach(listener => {
            try {
                listener(this.targetLanguage);
            }
            catch (error) {
                console.error('Failed to notify target language listeners:', error);
            }
        });
    }
    /**
     * 订阅源语言变化
     * @param listener 变化监听函数
     * @returns 取消订阅的函数
     */
    subscribeToSourceLanguage(listener) {
        this.sourceLanguageListeners.push(listener);
        return () => {
            this.sourceLanguageListeners = this.sourceLanguageListeners.filter(l => l !== listener);
        };
    }
    /**
     * 订阅目标语言变化
     * @param listener 变化监听函数
     * @returns 取消订阅的函数
     */
    subscribeToTargetLanguage(listener) {
        this.targetLanguageListeners.push(listener);
        return () => {
            this.targetLanguageListeners = this.targetLanguageListeners.filter(l => l !== listener);
        };
    }
    /**
     * 获取当前活动的文档身份标识符。
     * 直接从DocumentIdentity服务获取，确保一致性。
     */
    getCurrentDocumentIdentity() {
        const documentIdentityService = DocumentIdentityService.getInstance();
        const currentIdentity = documentIdentityService.state.currentIdentity; // This can be DocumentIdentifier | null
        return currentIdentity === null ? undefined : currentIdentity;
    }
    /**
     * 设置当前文档ID并加载该文档的语言设置
     * 通过DocumentIdentity服务激活文档
     * @param identity 统一的文档身份标识符
     * @returns 是否成功激活文档
     */
    setCurrentDocument(identity) {
        // 如果ID或类型为空 (虽然类型系统会保证 identity 对象结构，但可以加一道防御)
        if (!identity || identity.id === null || !identity.type) {
            console.warn('[LanguageStateManager] setCurrentDocument called with invalid identity:', identity);
            return false;
        }
        const documentIdentityService = DocumentIdentityService.getInstance();
        const currentServiceState = documentIdentityService.state;
        // Check if the document to be set is already the active document
        if (currentServiceState.currentIdentity &&
            currentServiceState.currentIdentity.id === identity.id &&
            currentServiceState.currentIdentity.type === identity.type) {
            return true; // Already active, conceptually a success
        }
        // 激活文档ID (透传到DocumentIdentityService)
        // Pass the full identity object. Metadata remains the same.
        return documentIdentityService.activateDocument(identity, DocumentIdentitySource.MANUAL, {
            source: 'language-manager',
        });
        // 注意：不需要在这里加载文档语言设置
        // 因为语言状态的恢复和特定文档语言的应用
        // 将由UI层（如TextReadingPageRouteWrapper）在确认核心数据加载完毕后，
        // 通过调用 applyLanguageSettingsFromLoadedDocument 来显式完成。
    }
    /**
     * 设置目标语言。仅更新内存状态。
     * @param languageCode 目标语言代码
     * @param _documentId (可选) 文档ID，在此版本中被忽略
     */
    async setTargetLanguageWithSync(languageCode, _documentId // Renamed to indicate it's ignored
    ) {
        if (this.targetLanguage !== languageCode) {
            this.targetLanguage = languageCode;
            this.notifyTargetLanguageListeners();
        }
        return { success: true };
    }
    /**
     * 设置源语言。仅更新内存状态。
     * @param languageCode 源语言代码
     * @param _documentId (可选) 文档ID，在此版本中被忽略
     */
    async setSourceLanguageWithSync(languageCode, _documentId // Renamed to indicate it's ignored
    ) {
        if (this.sourceLanguage !== languageCode) {
            this.sourceLanguage = languageCode;
            this.notifySourceLanguageListeners();
        }
        return { success: true };
    }
    /**
     * 处理文档身份变更事件
     * @param event 文档身份事件
     */
    handleDocumentIdentityChange(event) {
        switch (event.type) {
            case DocumentIdentityEventType.ACTIVATED: {
                if (event.identity) {
                    // 文档激活时，语言状态不再从此路径隐式更新。
                    // 全局语言状态的文档特异性设置将由UI层在数据加载后显式触发。
                }
                break;
            }
            case DocumentIdentityEventType.DEACTIVATED: {
                const targetChanged = this.targetLanguage !== DEFAULTS.TARGET_LANGUAGE;
                this.targetLanguage = DEFAULTS.TARGET_LANGUAGE;
                const sourceChanged = this.sourceLanguage !== DEFAULTS.SOURCE_LANGUAGE;
                this.sourceLanguage = DEFAULTS.SOURCE_LANGUAGE;
                if (targetChanged) {
                    this.notifyTargetLanguageListeners();
                }
                if (sourceChanged) {
                    this.notifySourceLanguageListeners();
                }
                break;
            }
            default:
                break;
        }
    }
    /**
     * @deprecated This method is no longer used as language settings are now applied
     *             explicitly by the UI layer (e.g., TextReadingPageRouteWrapper) after
     *             document data (including language info) is loaded, by calling
     *             `applyLanguageSettingsFromLoadedDocument`.
     */
    loadDocumentLanguageSettings() {
        // 方法体已清空
    }
    /**
     * 显式应用从已加载文档数据中获取的语言设置。
     * 此方法应由UI层（例如 TextReadingPageRouteWrapper）在核心文档数据
     * (包含 sourceLanguage 和 targetLanguage) 从存储中加载完毕后调用。
     *
     * @param identity 当前文档的身份标识符 (主要用于日志和未来可能的校验)。
     * @param sourceLanguage 从文档数据中加载的源语言。
     * @param targetLanguage 从文档数据中加载的目标语言。
     */
    applyLanguageSettingsFromLoadedDocument(identity, sourceLanguage, targetLanguage) {
        let sourceActuallyChanged = false;
        if (this.sourceLanguage !== sourceLanguage) {
            this.sourceLanguage = sourceLanguage;
            sourceActuallyChanged = true;
        }
        let targetActuallyChanged = false;
        if (this.targetLanguage !== targetLanguage) {
            this.targetLanguage = targetLanguage;
            targetActuallyChanged = true;
        }
        if (sourceActuallyChanged) {
            this.notifySourceLanguageListeners();
        }
        if (targetActuallyChanged) {
            this.notifyTargetLanguageListeners();
        }
    }
    async updateUserPreferredTargetLanguage(identity, newTargetLanguage) {
        const logPrefix = '[LanguageStateManager updateUserPreferredTargetLanguage]';
        if (!identity || !identity.id || !identity.type) {
            console.error(`${logPrefix} Invalid identity provided:`, identity);
            throw new Error('Invalid identity provided for updating target language.');
        }
        const originalTargetLanguage = this.targetLanguage; // Store original for potential revert
        let stateActuallyChanged = false;
        if (this.targetLanguage !== newTargetLanguage) {
            this.targetLanguage = newTargetLanguage;
            stateActuallyChanged = true;
        }
        try {
            // The payload for updating the document should be specific to the targetLanguage change.
            const updatePayload = { targetLanguage: newTargetLanguage };
            if (identity.type === 'text') {
                const textStorageProvider = TextDocumentStorageProvider.getInstance();
                await textStorageProvider.updateDocument(identity, updatePayload);
            }
            else if (identity.type === 'pdf') {
                const pdfStorageProvider = PdfDocumentStorageProvider.getInstance();
                await pdfStorageProvider.updateDocument(identity, updatePayload);
            }
            else {
                console.warn(`${logPrefix} Unknown document type: ${identity.type}. Target language persistence skipped.`);
                // If state was changed in memory for an unhandled type, revert it.
                if (stateActuallyChanged) {
                    this.targetLanguage = originalTargetLanguage;
                    // No need to notify listeners as it was a transient change for an unsupported type.
                    stateActuallyChanged = false; // Ensure listeners are not notified below
                }
                // Optionally throw an error if unknown types should explicitly not occur
                // throw new Error(`Unsupported document type: ${identity.type}`);
            }
            if (stateActuallyChanged) {
                this.notifyTargetLanguageListeners();
            }
        }
        catch (error) {
            console.error(`${logPrefix} Failed to update target language for doc ID ${identity.id} (type: ${identity.type}):`, error);
            // Revert in-memory change if persistence fails
            if (stateActuallyChanged) {
                this.targetLanguage = originalTargetLanguage;
                // Not notifying listeners here, as the UI might have already reflected the change,
                // and then an error message would be shown. Reverting silently.
            }
            throw error; // Rethrow error for the caller to handle
        }
    }
}
//# sourceMappingURL=LanguageStateManager.js.map